import { Grid, Alert, AlertTitle, Divider, Typography, Tooltip, AlertColor } from '@mui/material';
import { t } from 'i18next';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { getInvoiceDeliveryMethodStringText, InvoiceDeliveryMethodStringEnum } from 'src/shared/models/enums/invoiceDeliveryMethodString.enum';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useParams } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { SendingSessionStatusEnum } from 'src/shared/models/enums/sendingSessionStatus.enum';
import { getSendingSessionTypeText, InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { useMemo } from 'react';
import { SendingSessionSectionBtnHandler } from './sendingSessionSectionBtnHandler';
import { convertSendingTypeIntToString } from './invoiceMissionSendingTypeConverter';

type SendingSessionSectionProps = {
  invoiceMission: InvoiceMissionResponse;
  loading: boolean;
  isFSVersion2: boolean;
  refetchMission: (refetch: boolean) => void;
};

export const SendingSessionSection = (props: SendingSessionSectionProps) => {
  const { invoiceMission, loading, isFSVersion2, refetchMission } = props;
  const { sendingType } = useParams<{ sendingType: string }>();
  const parsedSendingType = Number(sendingType);

  // Ta bort sendings med status "Created" från sendingSessions
  const sentSessions = invoiceMission.sendingSessions.filter((snd) => snd.status !== SendingSessionStatusEnum.Created);
  const totalSentInvoices = invoiceMission.sendingSessions
    .filter((sending) => sending.status !== SendingSessionStatusEnum.Created)
    .reduce((sum, sending) => sum + (sending.sendingsCount || 0), 0);

  const kind = useMemo(() => convertSendingTypeIntToString(parsedSendingType), [parsedSendingType]);
  const data = useMemo(() => invoiceMission.dataList?.find((d) => d.sendingType === kind), [invoiceMission.dataList, kind]);

  const approval = data?.approval;

  const isRejected = Boolean(approval?.approvalRequired && approval?.deniedAt);
  const deniedAt = isRejected ? DateHelper.getDateTime(approval?.deniedAt) : '';
  const deniedBy = isRejected ? approval?.deniedBy ?? '' : '';

  const isPendingApproval =
    parsedSendingType !== InvoiceSendingTypeIntegerEnum.NotSet && Boolean(approval?.approvalRequired && !approval?.approvedAt && !approval?.deniedAt);

  const getSendingCompleteStatusMessage = () => {
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Main) {
      return t('invoice:invoiceMissionDetailTabs.mainSending');
    }
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder1) {
      return t('invoice:invoiceMissionDetailTabs.reminderOne');
    }
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder2) {
      return t('invoice:invoiceMissionDetailTabs.reminderTwo');
    }
    return '';
  };

  const sendingLabel = getSendingCompleteStatusMessage() || t('invoice:invoiceMissionPage.sendingGenericName');

  const getSeverityAndAlertTitle = (): {
    severity: AlertColor;
    alertTitle: string;
  } => {
    if (isRejected) {
      return {
        severity: 'error',
        alertTitle: t('invoice:invoiceMissionPage.rejectedStatusMessage', {
          sending: sendingLabel,
          deniedAt,
          deniedBy,
        }),
      };
    }

    if (isPendingApproval) {
      return { severity: 'info', alertTitle: t('invoice:invoiceMissionPage.waitingApprove') };
    }

    if (!invoiceMission || parsedSendingType === InvoiceSendingTypeIntegerEnum.NotSet) {
      return { severity: 'info', alertTitle: t('invoice:invoiceMissionPage.sendingSessionOverview') };
    }

    if (sentSessions.length === 0) {
      return {
        severity: 'info',
        alertTitle: t('invoice:invoiceMissionPage.noSendingsAtm'),
      };
    }

    if (totalSentInvoices > 0 && totalSentInvoices < invoiceMission.data.totalCount) {
      return {
        severity: 'warning',
        alertTitle: t('invoice:invoiceMissionPage.sendingNotFullfilled', {
          totalSentInvoices,
          totalInvoices: invoiceMission.data.totalCount,
        }),
      };
    }

    if (totalSentInvoices >= invoiceMission.data.totalCount) {
      return {
        severity: 'success',
        alertTitle: t('invoice:invoiceMissionPage.sendingCompleteWithType', { reminder: getSendingCompleteStatusMessage() }),
      };
    }
    return {
      severity: 'info',
      alertTitle: '',
    };
  };
  const { severity, alertTitle } = getSeverityAndAlertTitle();

  const displayApproval = () => {
    if (approval) {
      const isApproved = approval.approvalRequired && approval.approvedAt && approval.approvedBy;

      if (isApproved) {
        return (
          <Alert
            severity='success'
            sx={{ mb: -1, border: (thm) => `1px solid ${thm.palette.divider}`, '& .MuiAlert-message': { width: '100% !important' } }}
          >
            {t('invoice:invoiceMissionPage.alertIsApprovedBy', {
              approvedBy: approval.approvedBy,
              approvedAt: DateHelper.getDateTime(approval.approvedAt),
              deadline: DateHelper.getDate(approval.deadline),
            })}
          </Alert>
        );
      }
    }

    return null;
  };

  return (
    <GridWrapper>
      <Grid item xs={12}>
        {isFSVersion2 ? (
          <>
            {displayApproval()}
            <Alert
              severity={severity}
              sx={{ mt: 2, border: (thm) => `1px solid ${thm.palette.divider}`, '& .MuiAlert-message': { width: '100% !important' } }}
            >
              <AlertTitle>{alertTitle}</AlertTitle>
              {!isRejected && <SendingSessionSectionBtnHandler loading={loading} invoiceMission={invoiceMission} refetchMission={refetchMission} />}

              {/* Labels */}
              {sentSessions && sentSessions.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ marginBottom: 1, marginTop: 0 }} />
                  </Grid>
                  <Grid container mb={1} direction='row' justifyContent='space-between'>
                    <Grid item xs={8} container justifyContent='space-between'>
                      <Grid item xs={1}>
                        <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {t('invoice:invoiceMissionPage.quantity')}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {t('invoice:invoiceMissionPage.type')}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {t('invoice:invoiceMissionPage.method')}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {t('invoice:invoiceMissionPage.sign')}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        {t('invoice:invoiceMissionPage.date')}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
              {/* Label Inputs */}
              {sentSessions &&
                sentSessions
                  .slice() // kopiera så vi inte muterar originalet
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((sending, index) => (
                    <Grid container key={sending.session_id} direction='row' alignItems='center' justifyContent='space-between'>
                      <Grid item xs={8} container justifyContent='space-between'>
                        <Grid item xs={1}>
                          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {t('invoice:invoiceMissionPage.invoiceCounter', { count: sending.sendingsCount })}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {getSendingSessionTypeText(sending.type as InvoiceSendingTypeStringEnum, t)}
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {getInvoiceDeliveryMethodStringText(sending.deliveryMethod as InvoiceDeliveryMethodStringEnum, t)}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Tooltip title={sending.sign || ''} arrow>
                            <Typography
                              variant='caption'
                              sx={{
                                marginTop: 0.3,
                                fontWeight: 500,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '120px',
                                display: 'inline-block',
                              }}
                            >
                              {sending.sign}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Grid>

                      <Grid item xs={2}>
                        <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {DateHelper.getDateTime(sending.createdAt)}
                        </Typography>
                      </Grid>

                      {index !== sentSessions.length - 1 && (
                        <Grid item xs={12}>
                          <Divider sx={{ marginBottom: 1, marginTop: 1, marginLeft: 1, marginRight: 1 }} />
                        </Grid>
                      )}
                    </Grid>
                  ))}
            </Alert>
          </>
        ) : (
          <Alert sx={{ padding: 2 }} severity='warning'>
            {t('invoice:invoiceMissionPage.missingSendingData')}
          </Alert>
        )}
      </Grid>
    </GridWrapper>
  );
};
